import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  TextField,
  Button,
  MenuItem,
  Select,
  FormHelperText,
  Checkbox,
} from "@mui/material";

import { fields } from "./UserFormConfig";

function UserForm({ onSubmit, initialData = {} }) {
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // ----------------------------------------
  // FIELD VISIBILITY RULES (fixed version)
  // ----------------------------------------
  const shouldShowField = (fieldKey) => {
    const role = formData.role;

    const alwaysVisible = [
      "fullName",
      "email",
      "role",
      "sendWelcomeEmail",
      "requireTraining"
    ];

    if (alwaysVisible.includes(fieldKey)) return true;

    if (!role) return false;

    if (fieldKey === "function") {
      return ["function_manager", "route_manager", "area_manager"].includes(role);
    }

    if (fieldKey === "route") {
      return ["route_manager", "area_manager"].includes(role);
    }

    if (fieldKey === "area") {
      return ["area_manager"].includes(role);
    }

    return true;
  };

  // ----------------------------------------
  // Load initial data
  // ----------------------------------------
  useEffect(() => {
    if (Object.keys(initialData).length > 0) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        requireTraining: !initialData.hasSeenTrainingVideo,
      }));
    } else {
      setFormData({
        sendWelcomeEmail: false,
        requireTraining: true,
      });
    }
  }, [initialData]);

  // ----------------------------------------
  // Validation
  // ----------------------------------------
  const validateForm = () => {
    const errors = {};

    fields.forEach((field) => {
      if (field.validation === "required" && shouldShowField(field.key)) {
        if (!formData[field.key]) {
          errors[field.key] = `${field.name} is required`;
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ----------------------------------------
  // Submit
  // ----------------------------------------
  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      ...formData,
      hasSeenTrainingVideo: !formData.requireTraining,
    };

    delete payload.requireTraining;

    onSubmit(payload);
  };

  // ----------------------------------------
  // Field change
  // ----------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <Box>
      {fields.map((field) => {
        if (!shouldShowField(field.key)) return null;

        const errorMsg = formErrors[field.key];

        return (
          <FormControl
            key={field.key}
            fullWidth
            error={Boolean(errorMsg)}
            sx={{ mb: 2 }}
          >
            {field.type === "checkbox" ? (
              <Box display="flex" alignItems="center">
                <Checkbox
                  name={field.key}
                  checked={formData[field.key] ?? false}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      [field.key]: e.target.checked,
                    })
                  }
                />
                <FormLabel>{field.name}</FormLabel>
              </Box>
            ) : (
              <>
                <FormLabel>{field.name}</FormLabel>

                {field.type === "select" ? (
                  <Select
                    name={field.key}
                    value={formData[field.key] || ""}
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>Select an option</em>
                    </MenuItem>
                    {field.options?.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <TextField
                    name={field.key}
                    type={field.type}
                    placeholder={field.placeholder}
                    value={formData[field.key] || ""}
                    onChange={handleChange}
                    fullWidth
                    disabled={
                      Object.keys(initialData).length > 0 &&
                      (field.key === "fullName" || field.key === "email")
                    }
                  />
                )}
              </>
            )}

            {errorMsg && <FormHelperText>{errorMsg}</FormHelperText>}
          </FormControl>
        );
      })}

      <Box mt={3}>
        <Button variant="contained" fullWidth onClick={handleSubmit}>
          Submit
        </Button>
      </Box>
    </Box>
  );
}

export default UserForm;
